import { axiosClient } from '@/hooks';
import { Voter } from '@/interfaces';
import { SearchFormValues } from '@/pages/home/schema';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../store';

interface VoterResponse {
  statusCode: number;
  message: string;
  data: {
    voter: Voter;
    token?: string;
  };
}

interface VerifyOtpPayload {
  nationalId: string;
  otpCode: string;
}

interface SelfTransferPayload {
  villageId: string;
}

interface CreateNewVoterPayload {
  firstName: string;
  lastName: string;
  fatherName: string;
  motherName: string;
  email?: string;
  phoneNumber?: string;
  votingVillageId: string;
  originalVillageId?: string;
  genderId: string;
  dateOfBirth: string;
  placeOfBirth?: string;
  address?: string;
  passportNumber?: string;
  drivingLicenseNumber?: string;
  otherIdType?: string;
  otherIdNumber?: string;
  reason?: string;
}

// Initialize voter search and send OTP
export const initiateVoterSearch = createAsyncThunk(
  'voter/initiateSearch',
  async (searchData: SearchFormValues, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/voters/initiate', searchData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to initiate voter search');
    }
  }
);

// Verify OTP and retrieve voter data
export const verifyOtp = createAsyncThunk(
  'voter/verifyOtp',
  async (payload: VerifyOtpPayload, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post<VoterResponse>('/voters/verify', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to verify OTP');
    }
  }
);

// Perform self transfer
export const selfTransfer = createAsyncThunk(
  'voter/selfTransfer',
  async (payload: SelfTransferPayload, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      const token = state.voters.token;

      if (!token) {
        return rejectWithValue('Authentication token is missing');
      }

      const response = await axiosClient.patch('/voters/self-transfer', payload, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to perform self transfer');
    }
  }
);

// Create new voter registration
export const createNewVoter = createAsyncThunk(
  'voter/createNewVoter',
  async (payload: CreateNewVoterPayload, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/voter-annexes/new-voter', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to create new voter registration'
      );
    }
  }
);
